import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Instagram, Linkedin, Youtube, Mail, ArrowRight } from "lucide-react";
import { Reveal, Overline, MaskLines } from "../components/Motion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { SOCIALS } from "../lib/data";
import { sendForm } from "../lib/forms";

const REASONS = ["Brand Partnership", "Workshop Inquiry", "Service Inquiry", "Press", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", reason: "", message: "" });
  const [loading, setLoading] = useState(false);
  const botRef = useRef(null); // hidden spam trap: humans never tick it

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.reason || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await sendForm({
        subject: `New enquiry: ${form.reason}, from ${form.name}`,
        from_name: "Ethikally website",
        name: form.name,
        email: form.email,
        reason: form.reason,
        message: form.message,
        botcheck: botRef.current?.checked || false,
      });
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", reason: "", message: "" });
    } catch {
      toast.error(`Something went wrong. Please email us at ${SOCIALS.email}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="pt-40 md:pt-52 pb-16 relative z-10" data-testid="contact-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Overline>Contact</Overline>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-6 font-medium">
            <MaskLines lines={["Get in", <span key="e" className="italic text-forest">touch.</span>]} delay={0.1} />
          </h1>
        </div>
      </section>

      <section className="pb-28 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-16">
          {/* Form */}
          <div className="md:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              {/* Spam trap, invisible to visitors */}
              <input
                ref={botRef}
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ display: "none" }}
              />

              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label className="font-mono text-xs tracking-[0.15em] uppercase text-navy/50">Name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name"
                    className="w-full mt-2 bg-transparent border-b border-navy/20 focus:border-forest outline-none py-3 text-navy transition-colors"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs tracking-[0.15em] uppercase text-navy/50">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email"
                    className="w-full mt-2 bg-transparent border-b border-navy/20 focus:border-forest outline-none py-3 text-navy transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="font-mono text-xs tracking-[0.15em] uppercase text-navy/50">Reason</label>
                <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                  <SelectTrigger
                    data-testid="contact-reason"
                    className="w-full mt-2 bg-transparent border-0 border-b border-navy/20 rounded-none px-0 py-3 h-auto focus:ring-0 focus:border-forest text-navy"
                  >
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => (
                      <SelectItem key={r} value={r} data-testid={`reason-${r.toLowerCase().replace(/\s/g, "-")}`}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-mono text-xs tracking-[0.15em] uppercase text-navy/50">Message</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="w-full mt-2 bg-transparent border-b border-navy/20 focus:border-forest outline-none py-3 text-navy transition-colors resize-none"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group rounded-full bg-navy text-cream px-8 py-4 font-medium inline-flex items-center gap-3 hover:bg-forest transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? "Sending…" : "Send message"}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="mt-4 text-sm text-navy/50">
                  We use your details only to respond to your enquiry.
                </p>
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-5 md:pl-10 md:border-l border-navy/10">
            <Reveal>
              <h3 className="font-heading text-3xl tracking-tight">Prefer email?</h3>
              <a href={`mailto:${SOCIALS.email}`} data-testid="contact-email-link" className="group inline-flex items-center gap-3 mt-4 text-forest text-lg">
                <Mail size={20} /> {SOCIALS.email}
              </a>

              <h4 className="font-mono text-xs tracking-[0.2em] uppercase text-navy/50 mt-12 mb-5">Follow along</h4>
              <div className="space-y-4">
                <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="group flex items-center gap-3 text-navy hover:text-forest transition-colors">
                  <Instagram size={20} /> @ethikally
                </a>
                <a href={SOCIALS.linkedinHemant} target="_blank" rel="noreferrer" data-testid="contact-linkedin-hemant" className="group flex items-center gap-3 text-navy hover:text-forest transition-colors">
                  <Linkedin size={20} /> Hemant Maheshwari
                </a>
                <a href={SOCIALS.linkedinNandini} target="_blank" rel="noreferrer" data-testid="contact-linkedin-nandini" className="group flex items-center gap-3 text-navy hover:text-forest transition-colors">
                  <Linkedin size={20} /> Nandini Malpani
                </a>
                <a href={SOCIALS.youtube} target="_blank" rel="noreferrer" data-testid="contact-youtube" className="group flex items-center gap-3 text-navy hover:text-forest transition-colors">
                  <Youtube size={20} /> @ethikally on YouTube
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
